import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogService, RentalBookingBoardEntry } from './catalog.service';

type CalendarBlock = {
  kind: 'booking' | 'agreement';
  label: string;
  status: string;
  startUtc: string;
  endUtc: string;
  link?: string;
};

@Component({
  standalone: true,
  selector: 'ain-rental-booking-board',
  imports: [AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, SelectModule],
  template: `
    <ain-page [title]="t('catalog.rental.bookingBoard.title')" [description]="t('catalog.rental.bookingBoard.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" icon="pi pi-chevron-left" severity="secondary" (click)="shiftWindow(-1)"></button>
            <button pButton type="button" [label]="t('catalog.rental.calendar.today')" severity="secondary" (click)="goToday()"></button>
            <button pButton type="button" icon="pi pi-chevron-right" severity="secondary" (click)="shiftWindow(1)"></button>
            <span class="window-label">{{ windowLabel() }}</span>
            <p-select
              [options]="windowOptions"
              [(ngModel)]="windowDays"
              (ngModelChange)="load()"
              optionLabel="label"
              optionValue="value"
              appendTo="body" />
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
            <a routerLink="/rental/agreements">{{ t('catalog.rental.agreements.title') }}</a>
          </div>

          <div class="legend">
            <span class="chip booking">{{ t('catalog.rental.calendar.legendBooking') }}</span>
            <span class="chip agreement">{{ t('catalog.rental.calendar.legendAgreement') }}</span>
          </div>

          @if (rows().length === 0) {
            <ain-state type="empty" [title]="t('catalog.rental.bookingBoard.noUnits')" />
          } @else {
            <div class="calendar-scroll">
              <div class="calendar-grid" [style.--day-count]="dayHeaders().length">
                <div class="corner">{{ t('catalog.rental.calendar.unitColumn') }}</div>
                @for (day of dayHeaders(); track day.key) {
                  <div class="day-head" [class.weekend]="day.isWeekend">
                    <strong>{{ day.label }}</strong>
                    <small>{{ day.subLabel }}</small>
                  </div>
                }

                @for (unit of rows(); track unit.rentalAssetUnitId) {
                  <div class="unit-label">
                    <strong>{{ unit.unitCode }}</strong>
                    <small>{{ unit.unitName }} · {{ unit.unitStatus }}</small>
                  </div>
                  <div class="unit-track">
                    <div class="day-grid">
                      @for (day of dayHeaders(); track day.key) {
                        <div class="day-cell" [class.weekend]="day.isWeekend"></div>
                      }
                      @for (block of blocksForUnit(unit); track block.label + block.startUtc) {
                        @if (block.link) {
                          <a
                            class="block"
                            [class.booking]="block.kind === 'booking'"
                            [class.agreement]="block.kind === 'agreement'"
                            [style.left.%]="blockLeftPercent(block)"
                            [style.width.%]="blockWidthPercent(block)"
                            [routerLink]="block.link"
                            [title]="block.label + ' · ' + block.status">
                            {{ block.label }}
                          </a>
                        } @else {
                          <span
                            class="block"
                            [class.booking]="block.kind === 'booking'"
                            [class.agreement]="block.kind === 'agreement'"
                            [style.left.%]="blockLeftPercent(block)"
                            [style.width.%]="blockWidthPercent(block)"
                            [title]="block.label + ' · ' + block.status">
                            {{ block.label }}
                          </span>
                        }
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 12px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .window-label { font-weight: 600; color: #334155; min-width: 180px; }
    a { color: #1d4ed8; font-weight: 600; text-decoration: none; margin-inline-start: auto; }
    .legend { display: flex; gap: 8px; flex-wrap: wrap; }
    .chip { font-size: 12px; padding: 4px 10px; border-radius: 999px; font-weight: 600; }
    .chip.booking { background: #dbeafe; color: #1d4ed8; }
    .chip.agreement { background: #dcfce7; color: #15803d; }
    .calendar-scroll { overflow-x: auto; border: 1px solid #e2e8f0; border-radius: 12px; }
    .calendar-grid {
      display: grid;
      grid-template-columns: 220px repeat(var(--day-count), minmax(56px, 1fr));
      min-width: max(100%, calc(220px + var(--day-count) * 56px));
    }
    .corner, .day-head, .unit-label {
      background: #f8fafc;
      border-bottom: 1px solid #e2e8f0;
      padding: 8px;
    }
    .corner { font-weight: 600; font-size: 12px; color: #475569; }
    .day-head { text-align: center; display: grid; gap: 2px; font-size: 11px; }
    .day-head.weekend { background: #f1f5f9; }
    .day-head strong { font-size: 12px; color: #0f172a; }
    .day-head small { color: #64748b; }
    .unit-label {
      border-bottom: 1px solid #eef2f7;
      display: grid;
      gap: 2px;
    }
    .unit-label strong { font-size: 13px; }
    .unit-label small { color: #64748b; font-size: 11px; }
    .unit-track { border-bottom: 1px solid #eef2f7; min-height: 56px; position: relative; grid-column: 2 / -1; }
    .day-grid { display: grid; grid-template-columns: repeat(var(--day-count), minmax(56px, 1fr)); height: 100%; position: relative; }
    .day-cell { border-inline-end: 1px solid #f1f5f9; min-height: 56px; }
    .day-cell.weekend { background: #fafafa; }
    .block {
      position: absolute;
      top: 8px;
      height: 40px;
      border-radius: 8px;
      padding: 4px 8px;
      font-size: 11px;
      font-weight: 600;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      color: #0f172a;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
    }
    .block.booking { background: #bfdbfe; border: 1px solid #93c5fd; }
    .block.agreement { background: #bbf7d0; border: 1px solid #86efac; }
    a.block { text-decoration: none; }
  `]
})
export class RentalBookingBoardComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly rows = signal<RentalBookingBoardEntry[]>([]);
  readonly windowStart = signal(this.startOfDay(new Date()));
  windowDays = 14;

  readonly windowOptions = [
    { label: '7', value: 7 },
    { label: '14', value: 14 },
    { label: '30', value: 30 }
  ];

  readonly dayHeaders = computed(() => {
    const start = this.windowStart();
    return Array.from({ length: this.windowDays }, (_, index) => {
      const date = this.addDays(start, index);
      const day = date.getDay();
      return {
        key: date.toISOString().slice(0, 10),
        label: date.toLocaleDateString(undefined, { weekday: 'short' }),
        subLabel: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        isWeekend: day === 0 || day === 6,
        date
      };
    });
  });

  readonly windowLabel = computed(() => {
    const days = this.dayHeaders();
    if (days.length === 0) return '';
    const first = days[0].date;
    const last = days[days.length - 1].date;
    return `${first.toLocaleDateString()} – ${last.toLocaleDateString()}`;
  });

  private readonly windowEnd = computed(() => {
    const start = this.windowStart();
    return this.addDays(start, this.windowDays);
  });

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading.set(true);
    const fromUtc = this.windowStart().toISOString();
    const toUtc = this.windowEnd().toISOString();
    this.service.getRentalBookingBoard(fromUtc, toUtc).subscribe({
      next: rows => { this.rows.set(rows); this.loading.set(false); },
      error: () => { this.rows.set([]); this.loading.set(false); }
    });
  }

  shiftWindow(direction: -1 | 1): void {
    this.windowStart.update(value => this.addDays(value, direction * this.windowDays));
    this.load();
  }

  goToday(): void {
    this.windowStart.set(this.startOfDay(new Date()));
    this.load();
  }

  blocksForUnit(unit: RentalBookingBoardEntry): CalendarBlock[] {
    const bookings = unit.bookings.map(item =>
      this.toBlock('booking', item.bookingReference, item.status, item.startUtc, item.endUtc));
    const agreements = unit.agreements.map(item =>
      this.toBlock('agreement', item.agreementNumber, item.status, item.startUtc, item.endUtc, `/rental/agreements/${item.id}`));
    return [...bookings, ...agreements];
  }

  blockLeftPercent(block: CalendarBlock): number {
    const windowStartMs = this.windowStart().getTime();
    const windowEndMs = this.windowEnd().getTime();
    const startMs = Math.max(new Date(block.startUtc).getTime(), windowStartMs);
    const span = windowEndMs - windowStartMs;
    if (span <= 0) return 0;
    return ((startMs - windowStartMs) / span) * 100;
  }

  blockWidthPercent(block: CalendarBlock): number {
    const windowStartMs = this.windowStart().getTime();
    const windowEndMs = this.windowEnd().getTime();
    const startMs = Math.max(new Date(block.startUtc).getTime(), windowStartMs);
    const endMs = Math.min(new Date(block.endUtc).getTime(), windowEndMs);
    const span = windowEndMs - windowStartMs;
    if (span <= 0 || endMs <= startMs) return 0;
    return ((endMs - startMs) / span) * 100;
  }

  t(key: string): string { return this.localization.translate(key); }

  private toBlock(
    kind: 'booking' | 'agreement',
    label: string,
    status: string,
    startUtc: string,
    endUtc: string,
    link?: string
  ): CalendarBlock {
    return { kind, label, status, startUtc, endUtc, link };
  }

  private startOfDay(value: Date): Date {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private addDays(value: Date, days: number): Date {
    const date = new Date(value);
    date.setDate(date.getDate() + days);
    return date;
  }
}
